import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Loader2, MessageSquare, Send, X } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, beDate, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { SectionCard } from "@/components/studio/StudioUI";

const QuestionItem = ({ q, canReply, onReplied, withLink }) => {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await studio.post(`/accounting/questions/${q.id}/reply`, { message: reply });
      onReplied(data);
      toast.success("Réponse envoyée au comptable par e-mail.");
    } catch (e) { toast.error(apiError(e, "Envoi impossible.")); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`accountant-question-${q.id}`} className={`rounded-xl border p-3.5 ${q.status === "ouverte" ? "border-amber-200 bg-amber-50/60" : "border-[#E2E7F7] bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#5C6A87]">
        <span>
          {withLink ? <Link to={`/studio/factures/${q.invoice_id}`} className="font-mono font-semibold text-[#3A5CC8] hover:underline">{q.invoice_reference}</Link> : <span className="font-mono font-semibold text-[#0F2645]">{q.invoice_reference}</span>}
          {q.client_name ? ` · ${q.client_name}` : ""} · {beDate(q.created_at)}
        </span>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${q.status === "ouverte" ? "border-amber-300 bg-white text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{q.status === "ouverte" ? "En attente" : "Répondue"}</span>
      </div>
      <p className="mt-2 text-sm text-[#0F2645]"><span className="font-semibold">Comptable :</span> {q.message}</p>
      {q.reply && <p data-testid={`accountant-question-reply-${q.id}`} className="mt-2 rounded-lg bg-[#F6F8FD] p-2.5 text-sm text-[#0F2645]"><span className="font-semibold">Réponse ({beDate(q.replied_at)}) :</span> {q.reply}</p>}
      {canReply && !q.reply && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input data-testid={`accountant-question-reply-input-${q.id}`} className={inputCls} placeholder="Votre réponse (envoyée par e-mail au comptable)" value={reply} onChange={(e) => setReply(e.target.value)} />
          <button type="button" data-testid={`accountant-question-reply-btn-${q.id}`} disabled={busy || !reply.trim()} onClick={send} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Répondre</button>
        </div>
      )}
    </div>
  );
};

/* Liste des questions du comptable (toutes, ou celles d'une facture). canReply = patron. */
export const AccountantQuestions = ({ invoiceId = "", canReply = false, emptyHidden = false, className = "mt-4" }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    studio.get(`/accounting/questions${invoiceId ? `?invoice_id=${invoiceId}` : ""}`).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [invoiceId]);
  if (items === null || (emptyHidden && !items.length)) return null;
  const open = items.filter((q) => q.status === "ouverte").length;
  return (
    <SectionCard
      testId="accountant-questions"
      className={className}
      title={<span className="inline-flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#3A5CC8]" /> Questions du comptable</span>}
      hint={open ? `${open} question${open > 1 ? "s" : ""} en attente de réponse` : items.length ? "Toutes répondues" : "Aucune question pour l'instant"}
    >
      {items.length ? (
        <div className="space-y-3">
          {items.map((q) => <QuestionItem key={q.id} q={q} canReply={canReply} withLink={canReply && !invoiceId} onReplied={(d) => setItems((l) => l.map((x) => (x.id === d.id ? { ...x, ...d } : x)))} />)}
        </div>
      ) : (
        <p data-testid="accountant-questions-empty" className="text-sm text-[#5C6A87]">Depuis la liste des factures, le bouton « ? » permet de poser une question sur une facture précise. L'entreprise est notifiée et vous répond par e-mail.</p>
      )}
    </SectionCard>
  );
};

/* Dialogue (côté comptable) : poser une question sur une facture. */
export const AskQuestionDialog = ({ invoice, onClose, onAsked }) => {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await studio.post("/accounting/questions", { invoice_id: invoice.id, message });
      toast.success(`Question envoyée : l'entreprise est notifiée pour ${invoice.reference}.`);
      onAsked?.(data);
      onClose();
    } catch (e) { toast.error(apiError(e, "Envoi impossible.")); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ask-question-dialog" className="fixed inset-0 z-[70] flex items-end justify-center bg-[#0F2645]/50 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="dialog">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl sm:p-6" style={{ animation: "devSlideUp .2s ease-out" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-heading text-lg font-bold text-[#0F2645] inline-flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#3A5CC8]" /> Question sur {invoice.reference}</p>
            <p className="mt-1 text-xs text-[#5C6A87]">{invoice.client_name} · l'entreprise reçoit une notification et vous répond par e-mail.</p>
          </div>
          <button type="button" data-testid="ask-question-close" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#93A0BC] hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <textarea data-testid="ask-question-input" rows={4} className={`${inputCls} mt-4`} placeholder="Ex. : le taux de TVA à 6 % est-il justifié pour ce chantier (logement > 10 ans) ?" value={message} onChange={(e) => setMessage(e.target.value)} />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Annuler</button>
          <button type="button" data-testid="ask-question-send" disabled={busy || message.trim().length < 3} onClick={send} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer</button>
        </div>
      </div>
    </div>
  );
};
